import React, {createContext, useContext} from 'react';

import {usePanelBankConfig} from '../../../state/views/panelBankConfig/hooks';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import type {PanelBankConfigWithRefs} from '../types';

const PanelBankConfigWithRefsValueContext = createContext<
  PanelBankConfigWithRefs | undefined
>(undefined);

/**
 * To access context values, please use {@link usePanelBankConfigWithRefs}.
 */
export const PanelBankConfigWithRefsProvider: React.FC<{}> = ({children}) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const panelBankConfig = usePanelBankConfig(panelBankConfigRef);

  return (
    <PanelBankConfigWithRefsValueContext.Provider value={panelBankConfig}>
      {children}
    </PanelBankConfigWithRefsValueContext.Provider>
  );
};

/**
 * **IMPORTANT** Consumers should destructure only the properties they need!
 *   const { sections, settings } = usePanelBankConfigWithRefs();
 *
 * ⚠️ Panel bank denormalization is expensive: this hook returns sections, panels,
 * AND settings (searchQuery, searchHistory, etc.) assembled from normalized state.
 * The `sections` array fetches ALL panels into a nested structure, so any panel
 * or section change triggers recomputation.
 *
 * Avoid triggering unnecessary re-renders by destructuring only what you need.
 * If you only need `settings`, destructure just that property to avoid
 * dependencies on the expensive `sections` array.
 *
 * Why destructure?
 * When you use a destructured value in a useEffect/useMemo/useCallback dependency array,
 * it only re-runs when that specific value changes, not when unrelated
 * properties in the config change. This prevents unnecessary effect re-runs
 * and keeps dependencies explicit and minimal.
 */
export function usePanelBankConfigWithRefs() {
  const context = useContext(PanelBankConfigWithRefsValueContext);
  if (context === undefined) {
    throw new Error(
      'usePanelBankConfigWithRefs must be used within a PanelBankConfigWithRefsProvider'
    );
  }

  return context;
}
